import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { itemService } from "../../api/services/itemService";
import { addToCart } from "../../utils/cartUtils";
import LoginRequiredModal from "../LoginRequiredModal/LoginRequiredModal";
import Loader from "../Loader/Loader";
import defaultProductImage from "../../images/Default-Image.jpeg";
import TruncatedTitle from "../TruncatedTitle/TruncatedTitle";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import "./CategoryPage.css";

const PAGE_SIZE = 8;

const CategoryPage = () => {
  const showPrices = useCanViewPrices();
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Reset state when category changes
  useEffect(() => {
    setSelectedProducts([]);
    setSelectedModel(null); // Reset selected model when category changes
    setCurrentPage(1);
    setHasMore(false);
    setLoadingMore(false);
  }, [categoryName]);

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    // If it's already a full URL, return as is
    if (
      typeof imagePath === "string" &&
      (imagePath.startsWith("http://") || imagePath.startsWith("https://"))
    ) {
      return imagePath;
    }
    // If it's a relative path starting with /, prepend the image base URL
    if (typeof imagePath === "string" && imagePath.startsWith("/")) {
      return `https://wireox.erp.gold${imagePath}`;
    }
    return imagePath;
  };

  // Function to fetch items from API
  const fetchItemsByModel = useCallback(async (modelName, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await itemService.getItemSearchWithAttributes(
        "Model",
        modelName,
        page,
        PAGE_SIZE,
      );
      if (response && response.Items) {
        // Transform API items to match the product format used in the component
        const transformedProducts = response.Items.map((item, index) => {
          // Handle image - can be object {ImagePath, AltAttribute} or string
          let imageUrl = "";
          if (
            item.Images &&
            Array.isArray(item.Images) &&
            item.Images.length > 0
          ) {
            const firstImage = item.Images[0];
            if (typeof firstImage === "object" && firstImage.ImagePath) {
              imageUrl = getImageUrl(firstImage.ImagePath);
            } else if (typeof firstImage === "string") {
              imageUrl = getImageUrl(firstImage);
            }
          }

          // Determine pricing: if DiscountPrice exists and is greater than 0, show it as current price and UnitPrice as original
          const discountPrice =
            item.DiscountPrice && item.DiscountPrice > 0
              ? item.DiscountPrice
              : null;
          const unitPrice =
            item.UnitPrice && item.UnitPrice > 0 ? item.UnitPrice : 0;

          const hasDiscount =
            discountPrice && unitPrice > 0 && unitPrice > discountPrice;
          const currentPrice = hasDiscount ? discountPrice : unitPrice;
          const originalPrice = hasDiscount ? unitPrice : null;

          return {
            id: item.ItemId || `${page}-${index + 1}`,
            name: item.ItemName || "",
            price: currentPrice,
            originalPrice: originalPrice,
            hasDiscount: hasDiscount,
            badge: item.Brand ? `GENUINE ${item.Brand}` : "",
            image: imageUrl || defaultProductImage,
            core: item.CoreCharge || "Without Core",
            sku: item.SKU || "",
            availableQty: item.AvailableQty || 0,
          };
        });
        setSelectedProducts((prev) =>
          append ? [...prev, ...transformedProducts] : transformedProducts,
        );

        // Reset quantities only on fresh load, preserve on "load more"
        if (!append) {
          setQuantities({});
        }

        const totalPages =
          Number(response?.Pagination?.TotalPages) ||
          Number(response?.pagination?.totalPages) ||
          null;
        const currentPageFromApi =
          Number(response?.Pagination?.CurrentPage) ||
          Number(response?.pagination?.currentPage) ||
          page;

        if (totalPages) {
          setHasMore(currentPageFromApi < totalPages);
        } else {
          // Fallback when total pages are not provided
          setHasMore(Array.isArray(response.Items) && response.Items.length === PAGE_SIZE);
        }
      }
    } catch (err) {
      console.error("Error fetching items:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  // Fetch items when model is selected from URL parameter
  useEffect(() => {
    const modelParam = searchParams.get("model");
    if (modelParam) {
      setSelectedModel(modelParam);
      setCurrentPage(1);
      fetchItemsByModel(modelParam, 1, false);
    }
  }, [searchParams, categoryName, fetchItemsByModel]);

  const handleLoadMore = () => {
    if (!selectedModel || loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchItemsByModel(selectedModel, nextPage, true);
  };

  const handleAddToCart = (productId) => {
    const product = selectedProducts.find((p) => p.id === productId);
    const quantity = quantities[productId] || 0;
    if (product && quantity > 0) {
      const result = addToCart(
        {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: quantity,
          image: product.image,
        },
        () => setShowLoginModal(true),
      );
      // If login is not required and item was added, reset quantity
      if (result) {
        setQuantities((prev) => ({
          ...prev,
          [productId]: 0,
        }));
      }
    }
  };

  const handleQuantityChange = (productId, change) => {
    const product = selectedProducts.find((p) => p.id === productId);
    if (!product) return;

    const availableQty = product.availableQty || 0;
    const currentQuantity = quantities[productId] || 0;
    const newQuantity = currentQuantity + change;

    // Don't allow quantity to go below 0 or above availableQty
    if (newQuantity < 0) return;
    if (newQuantity > availableQty) return;

    setQuantities((prev) => ({
      ...prev,
      [productId]: newQuantity,
    }));
  };

  // Handle product click - navigate to product detail page
  const handleProductClick = (itemId) => {
    if (itemId) {
      navigate(`/product-detail?ItemID=${itemId}`);
    }
  };

  return (
    <div className="wirerox-category-page">
      {/* Login Required Modal */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      {/* Loader Overlay */}
      {loading && <Loader />}

      {/* Header with Red Title and Icon */}
      <div className="wirerox-category-header">
        <div className="wirerox-title-container">
          <div className="wirerox-menu-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <h1 className="wirerox-category-title">
            {selectedModel
              ? `${categoryName?.toUpperCase() || ""} - ${selectedModel}`
              : categoryName?.toUpperCase() || ""}
          </h1>
        </div>
        <div className="wirerox-underline"></div>
      </div>

      {/* Products Grid */}
      <div className="wirerox-products-section">
        {error && (
          <div style={{ textAlign: "center", padding: "2rem", color: "red" }}>
            <p>{error}</p>
          </div>
        )}
        {!loading && !error && selectedProducts.length === 0 && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>No products found. Please select a model.</p>
          </div>
        )}
        <div className="cps-products-row">
          {selectedProducts.map((product) => {
            const qty = quantities[product.id] || 0;
            const availableQty = product.availableQty || 0;

            return (
              <div key={product.id} className="cps-product-card">
                {/* Badge */}
                {/* <div className="cps-badge" aria-hidden="true">
                  {genuineText ? <span>{genuineText}</span> : null}
                  {brandText ? brandText : null}
                </div> */}

                {/* Logo/Image area */}
                <div
                  className="cps-logo-container"
                  onClick={() => handleProductClick(product.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleProductClick(product.id);
                    }
                  }}
                >
                  <img
                    src={product.image || defaultProductImage}
                    alt={product.name}
                    className={`cps-product-logo-img ${(product.image || defaultProductImage) === defaultProductImage ? "cps-product-logo-img--default" : ""}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultProductImage;
                      e.target.classList.add("cps-product-logo-img--default");
                    }}
                  />
                </div>

                {/* Title */}
                <TruncatedTitle
                  className="cps-title"
                  onClick={() => handleProductClick(product.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleProductClick(product.id);
                    }
                  }}
                >
                  {product.name}
                </TruncatedTitle>

                {/* Prices */}
                {showPrices && (
                  <div className="cps-price-section">
                    <span className="cps-current-price">
                      ${product.price?.toFixed(2) || "0.00"}
                    </span>
                    {product.hasDiscount &&
                    product.originalPrice &&
                    product.originalPrice > product.price ? (
                      <span className="cps-old-price">
                        ${product.originalPrice.toFixed(2)}
                      </span>
                    ) : null}
                  </div>
                )}

                <span className="cps-sub-text">{product.core}</span>

                {/* Quantity + Add to cart */}
                {availableQty > 0 ? (
                  <>
                    <div className="cps-qty-selector">
                      <button
                        type="button"
                        className="cps-qty-btn"
                        onClick={() => handleQuantityChange(product.id, -1)}
                        disabled={qty <= 0}
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <div className="cps-qty-input">{qty}</div>
                      <button
                        type="button"
                        className="cps-qty-btn"
                        onClick={() => handleQuantityChange(product.id, 1)}
                        disabled={qty >= availableQty}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="button"
                      className="cps-add-cart-btn"
                      onClick={() => handleAddToCart(product.id)}
                      disabled={qty <= 0}
                    >
                      Add To Cart
                    </button>
                  </>
                ) : (
                  <div className="cps-out-of-stock">Out of Stock</div>
                )}
              </div>
            );
          })}
        </div>
        {hasMore && selectedProducts.length > 0 && !error && (
          <div className="cps-load-more-wrap">
            <button
              type="button"
              className="cps-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
